import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaitlistForm = ({ variant = 'inline', source = 'hero', darkMode = false }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      const response = await axios.post(`${API}/waitlist`, {
        email,
        source
      });

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      if (error.response?.data?.detail) {
        setMessage(typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Invalid email format');
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="w-full max-w-md" data-testid="waitlist-form-inline">
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`flex items-center justify-center gap-3 py-4 px-6 rounded-xl ${darkMode ? 'bg-emerald-500/20 border border-emerald-400/30' : 'bg-emerald-50 border border-emerald-200'}`}
          >
            <CheckCircle2 className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
            <span className={`font-medium text-sm ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{message}</span>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === 'error') setStatus('idle');
              }}
              placeholder="Enter your email"
              className={`flex-1 px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-200 ${
                darkMode 
                  ? 'bg-white/10 border-white/20 text-white placeholder-white/50 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20' 
                  : 'border-slate-200 text-slate-800 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100'
              }`}
              disabled={status === 'loading'}
              data-testid="waitlist-email-input"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className={`px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity duration-200 flex items-center justify-center gap-2 whitespace-nowrap ${
                darkMode 
                  ? 'bg-gradient-to-r from-violet-500 to-pink-500 text-white shadow-lg shadow-purple-500/30' 
                  : 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-200'
              }`}
              data-testid="waitlist-submit-btn"
            >
              {status === 'loading' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Get Early Access
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
      
      {status === 'error' && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-500 text-sm mt-2 text-center"
          data-testid="waitlist-error-message"
        >
          {message}
        </motion.p>
      )}
    </div>
  );
};

export default WaitlistForm;
